import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from "react";

/**
 * A `useState` variant that transparently substitutes a fallback value whenever the
 * stored state is considered "empty".
 *
 * The hook stores its own state (optionally seeded with `initialValue`), but the value
 * it returns is resolved through `fallbackValue`: whenever the fallback condition holds,
 * the returned value is `fallbackValue` rather than the raw stored state. By default the
 * fallback applies when the stored state is `undefined`, but any predicate can be supplied
 * via `fallbackCondition` (e.g. treat an empty string or empty array as "empty").
 *
 * The fallback is also applied to the "previous value" passed to functional updates, so an
 * updater such as `prev => prev + 1` operates on the resolved value rather than `undefined`.
 *
 * This is useful for controlled inputs or configuration where a nullable/optional source
 * should always present a concrete, usable value to consumers while still allowing the
 * underlying state to be genuinely unset.
 *
 * @template T - The type of the state value.
 * @param params - Configuration object.
 * @param [params.initialValue] - Initial state, or a lazy initializer function. May be
 *   `undefined`, in which case the fallback applies until a value is set.
 * @param params.fallbackValue - The value returned (and used as the functional-update base)
 *   whenever the fallback condition holds.
 * @param [params.fallbackCondition] - Predicate receiving the current stored state and the
 *   `fallbackValue`; when it returns `true`, `fallbackValue` is used. Defaults to
 *   `state === undefined`.
 * @returns A `[state, setState]` tuple mirroring `useState`, where `state` is always the
 *   resolved (fallback-aware) value and `setState` accepts a value or updater.
 *
 * @example
 * ```typescript
 * // Undefined initial state resolves to the fallback until set
 * const [name, setName] = useStateWithFallback({ fallbackValue: "Anonymous" });
 * // name === "Anonymous" initially
 * setName("Ada"); // name === "Ada"
 * ```
 *
 * @example
 * ```typescript
 * // Treat an empty string as "empty" via a custom condition
 * const [label, setLabel] = useStateWithFallback({
 *   initialValue: "",
 *   fallbackValue: "Untitled",
 *   fallbackCondition: (val) => !val,
 * });
 * // label === "Untitled" while the stored value is ""
 * ```
 *
 * @example
 * ```typescript
 * // Functional updates receive the resolved value, not the raw stored state
 * const [count, setCount] = useStateWithFallback({ fallbackValue: 10 });
 * setCount((prev) => prev + 1); // prev is 10 (the fallback), so count === 11
 * ```
 */
export const useStateWithFallback = <T>({
  initialValue,
  fallbackValue,
  fallbackCondition,
}: {
  initialValue?: T | (() => T);
  fallbackValue: T;
  fallbackCondition?: (val: T | undefined, fallbackValue: T) => boolean;
}): [T, Dispatch<SetStateAction<T>>] => {
  const [_state, _setState] = useState(initialValue);

  const shouldUseFallback = useMemo(() => {
    if (fallbackCondition) {
      return fallbackCondition(_state, fallbackValue);
    }
    return _state === undefined;
  }, [_state, fallbackCondition, fallbackValue]);
  const state = useMemo<T>(() => {
    return shouldUseFallback || _state === undefined ? fallbackValue : _state;
  }, [_state, fallbackValue, shouldUseFallback]);

  const fallbackValueRef = useRef(fallbackValue);
  fallbackValueRef.current = fallbackValue;
  const fallbackConditionRef = useRef(fallbackCondition);
  fallbackConditionRef.current = fallbackCondition;

  const setState = useCallback((newVal: SetStateAction<T>) => {
    _setState((_prev) => {
      const condition = fallbackConditionRef.current;
      const shouldFallback = condition ? condition(_prev, fallbackValueRef.current) : _prev === undefined;
      if (newVal instanceof Function) {
        const prev = shouldFallback || _prev === undefined ? fallbackValueRef.current : _prev;
        return newVal(prev);
      }
      return newVal;
    });
  }, []); // deps array needs to stay empty here to ensure setState is stable

  return [state, setState];
};
